// src/components/ui/v2/command-palette-v2.tsx
"use client";

import * as React from "react";
import { createPortal } from "react-dom";
import { Loader2, Search } from "lucide-react";
import { cn } from "@/lib/utils";
import { Z_INDEX } from "./z-index";

export interface CommandPaletteV2Item {
  id: string;
  label: React.ReactNode;
  /** false ⇒ seleccionar no cierra la paleta (p.ej. cambiar de página). */
  closeOnSelect?: boolean;
  onSelect: () => void;
}

export interface CommandPaletteV2Section {
  id: string;
  heading?: string;
  items: CommandPaletteV2Item[];
}

export interface CommandPaletteV2Props {
  open: boolean;
  /** Cierre idempotente: puede ser llamado más de una vez por selección. */
  onClose: () => void;
  search: string;
  onSearchChange: (value: string) => void;
  /** Secciones ya filtradas por el caller. */
  sections: CommandPaletteV2Section[];
  placeholder?: string;
  loading?: boolean;
  loadingLabel?: React.ReactNode;
  emptyState?: React.ReactNode;
  footer?: React.ReactNode;
  /** Escape: el caller decide (volver a root o cerrar). Default: onClose. */
  onEscape?: () => void;
}

export function CommandPaletteV2({
  open,
  onClose,
  search,
  onSearchChange,
  sections,
  placeholder,
  loading = false,
  loadingLabel,
  emptyState,
  footer,
  onEscape
}: CommandPaletteV2Props) {
  const [mounted, setMounted] = React.useState(false);
  const [shouldRender, setShouldRender] = React.useState(open);
  const [isVisible, setIsVisible] = React.useState(open);
  const [activeIndex, setActiveIndex] = React.useState(0);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const listRef = React.useRef<HTMLDivElement>(null);

  // El portal cae fuera del elemento que next-themes marca con `.dark`, así
  // que las utilidades `dark:` no disparan. Leemos la clase real del DOM y
  // observamos cambios (mismo patrón que ChatOverlay).
  const [isDark, setIsDark] = React.useState(false);
  React.useEffect(() => {
    const read = () =>
      setIsDark(
        document.documentElement.classList.contains("dark") ||
          document.body.classList.contains("dark")
      );
    read();
    const observer = new MutationObserver(read);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"]
    });
    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ["class"]
    });
    return () => observer.disconnect();
  }, []);

  // Aplana las secciones asignando a cada item su índice global de navegación;
  // el índice es estructural (no por id) para tolerar ids duplicados entre secciones.
  const { flatItems, indexedSections } = React.useMemo(() => {
    const flat: CommandPaletteV2Item[] = [];
    const indexed = sections.map((section) => ({
      ...section,
      items: section.items.map((item) => {
        const flatIndex = flat.length;
        flat.push(item);
        return { item, flatIndex };
      })
    }));
    return { flatItems: flat, indexedSections: indexed };
  }, [sections]);

  React.useEffect(() => {
    setMounted(true);
    return () => setMounted(false);
  }, []);

  // Animación de entrada/salida en dos pasos (mismo patrón que Modal v2).
  React.useEffect(() => {
    if (!mounted) return;
    if (open) {
      setShouldRender(true);
      setIsVisible(false);
      const openId = window.setTimeout(() => setIsVisible(true), 28);
      return () => window.clearTimeout(openId);
    }
    setIsVisible(false);
    const closeId = window.setTimeout(() => setShouldRender(false), 420);
    return () => window.clearTimeout(closeId);
  }, [mounted, open]);

  React.useEffect(() => {
    if (!mounted || !shouldRender) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [mounted, shouldRender]);

  React.useEffect(() => {
    if (!isVisible) return;
    // El retraso debe ser mayor que el flip de visibilidad (28ms) para que el
    // foco no apunte a un elemento todavía oculto.
    const focusId = window.setTimeout(() => inputRef.current?.focus(), 60);
    return () => window.clearTimeout(focusId);
  }, [isVisible]);

  React.useEffect(() => {
    setActiveIndex(0);
  }, [search, flatItems.length, open]);

  const handleSelect = React.useCallback(
    (item: CommandPaletteV2Item) => {
      item.onSelect();
      if (item.closeOnSelect !== false) onClose();
    },
    [onClose]
  );

  React.useEffect(() => {
    if (!open) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.isComposing) return;
      if (event.key === "Escape") {
        event.preventDefault();
        (onEscape ?? onClose)();
        return;
      }
      if (event.key === "ArrowDown" || event.key === "ArrowUp") {
        event.preventDefault();
        if (!flatItems.length) return;
        const delta = event.key === "ArrowDown" ? 1 : -1;
        setActiveIndex(
          (index) => (index + delta + flatItems.length) % flatItems.length
        );
        return;
      }
      if (event.key === "Enter") {
        event.preventDefault();
        const item = flatItems[activeIndex];
        if (item) handleSelect(item);
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [open, flatItems, activeIndex, handleSelect, onEscape, onClose]);

  React.useEffect(() => {
    listRef.current
      ?.querySelector(`[data-cpv2-index="${activeIndex}"]`)
      ?.scrollIntoView({ block: "nearest" });
  }, [activeIndex]);

  if (!mounted || !shouldRender) return null;

  return createPortal(
    <div
      className={cn(
        "fixed inset-0 flex items-start justify-center px-3 pt-[14vh] sm:pt-[18vh]",
        isDark && "dark"
      )}
      style={{ zIndex: Z_INDEX.MODAL }}
      aria-hidden={!isVisible}
    >
      <button
        type="button"
        aria-label="Cerrar paleta de comandos"
        className="absolute inset-0 cursor-default bg-[radial-gradient(circle_at_top,rgba(99,102,241,0.15),transparent_32%)] bg-black/45 backdrop-blur-[6px]"
        style={{
          opacity: isVisible ? 1 : 0,
          transition: "opacity 360ms cubic-bezier(0.16, 1, 0.3, 1)"
        }}
        onClick={onClose}
      />

      <div
        role="dialog"
        aria-modal="true"
        aria-label="Paleta de comandos"
        className={cn(
          "relative flex w-full max-w-xl flex-col overflow-hidden rounded-[24px]",
          "border border-slate-200/70 bg-white/95 backdrop-blur-xl",
          "shadow-[0_32px_100px_-18px_rgba(15,23,42,0.35),0_18px_48px_-18px_rgba(79,70,229,0.18),inset_0_1px_0_rgba(255,255,255,0.72)]",
          "dark:border-zinc-800/80 dark:bg-zinc-950/95 dark:shadow-[0_32px_100px_-18px_rgba(0,0,0,0.58),0_18px_48px_-18px_rgba(99,102,241,0.2),inset_0_1px_0_rgba(255,255,255,0.05)]",
          "will-change-transform"
        )}
        style={{
          opacity: isVisible ? 1 : 0,
          transform: isVisible
            ? "translateY(0px) scale(1)"
            : "translateY(26px) scale(0.93)",
          filter: isVisible
            ? "blur(0px) saturate(1)"
            : "blur(8px) saturate(1.08)",
          transition:
            "opacity 420ms cubic-bezier(0.16, 1, 0.3, 1), transform 460ms cubic-bezier(0.16, 1, 0.3, 1), filter 420ms cubic-bezier(0.16, 1, 0.3, 1)"
        }}
      >
        <div className="pointer-events-none absolute inset-0 rounded-[inherit] border border-white/50 dark:border-white/5" />

        <div className="relative z-10 flex min-h-0 flex-col">
          <div className="flex items-center gap-3 border-b border-slate-200/70 px-4 py-3.5 dark:border-zinc-800/90">
            <Search className="h-4 w-4 shrink-0 text-slate-400 dark:text-zinc-500" />
            <input
              ref={inputRef}
              value={search}
              onChange={(event) => onSearchChange(event.target.value)}
              placeholder={placeholder}
              className="w-full bg-transparent text-sm text-slate-900 outline-none placeholder:text-slate-400 dark:text-zinc-100 dark:placeholder:text-zinc-500"
              spellCheck={false}
              autoComplete="off"
            />
            <kbd className="rounded-md border border-slate-200 bg-slate-50 px-1.5 py-0.5 text-[10px] font-medium text-slate-400 dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-400">
              esc
            </kbd>
          </div>

          <div
            ref={listRef}
            role="listbox"
            className="max-h-[min(52vh,420px)] min-h-0 overflow-y-auto px-2 py-2"
          >
            {loading ? (
              <div className="flex items-center gap-2 px-3 py-6 text-sm text-slate-500 dark:text-zinc-400">
                <Loader2 className="h-4 w-4 animate-spin" />
                {loadingLabel}
              </div>
            ) : flatItems.length === 0 ? (
              <div className="px-3 py-6 text-sm text-slate-500 dark:text-zinc-400">
                {emptyState}
              </div>
            ) : (
              indexedSections.map((section) => (
                <div key={section.id} className="mb-1 last:mb-0">
                  {section.heading ? (
                    <p className="px-3 pb-1 pt-2 text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400 dark:text-zinc-500">
                      {section.heading}
                    </p>
                  ) : null}
                  {section.items.map(({ item, flatIndex }) => {
                    const isActive = flatIndex === activeIndex;
                    return (
                      <button
                        key={item.id}
                        type="button"
                        role="option"
                        aria-selected={isActive}
                        data-cpv2-index={flatIndex}
                        onClick={() => handleSelect(item)}
                        onMouseMove={() => setActiveIndex(flatIndex)}
                        className={cn(
                          "flex w-full items-center rounded-xl px-3 py-2 text-left text-sm text-slate-700 transition-colors dark:text-zinc-200",
                          isActive && "bg-slate-100 dark:bg-zinc-800/80"
                        )}
                      >
                        {item.label}
                      </button>
                    );
                  })}
                </div>
              ))
            )}
          </div>

          {footer ? (
            <div className="border-t border-slate-200/70 bg-white/80 px-4 py-2.5 text-slate-500 dark:border-zinc-800/90 dark:bg-zinc-950/80 dark:text-zinc-400">
              {footer}
            </div>
          ) : null}
        </div>
      </div>
    </div>,
    document.body
  );
}
